import axios from 'axios';
import { apiClient } from '@/lib/api-client';
import { ProfileRequiredError } from '@/modules/auth/domain/entities/auth.entity';
import type {
  Submission,
  SubmissionKind,
  SubmissionRequest,
} from '../../domain/entities/submission.entity';
import {
  fromApiSubmission,
  toApiAnswers,
} from '../mappers/submission-api.mapper';
import type { SubmissionDatasource } from '../repositories/submission.repository.impl';

/**
 * Os envios no Primum Core (`POST`/`GET /exams-capture/ensaio|gabarito`): um
 * recurso por tipo, com prova e ano no corpo ou na query. Exigem sessão com
 * perfil nesta plataforma — o token e o `x-scope-id` saem do `apiClient`.
 * Reenviar substitui.
 */
export function submissionRoute(kind: SubmissionKind) {
  return `/exams-capture/${kind}`;
}

/**
 * 403 nestas rotas é o `ScopedProfileGuard`: a sessão é de uma conta sem perfil
 * nesta plataforma. Vira `ProfileRequiredError`, que a tela trata pedindo o
 * cadastro curto.
 */
function toProfileRequired(error: unknown): never {
  if (axios.isAxiosError(error) && error.response?.status === 403) {
    throw new ProfileRequiredError();
  }
  throw error;
}

export class SubmissionApiDatasource implements SubmissionDatasource {
  async submit(kind: SubmissionKind, body: SubmissionRequest): Promise<void> {
    await apiClient
      .post(submissionRoute(kind), {
        exam: body.exam,
        edition: body.edition,
        answers: toApiAnswers(kind, body),
      })
      .catch(toProfileRequired);
  }

  /** `404` não é falha: é a resposta de quem ainda não enviou. */
  async getMine(
    kind: SubmissionKind,
    exam: string,
    edition: number,
  ): Promise<Submission | null> {
    try {
      const { data } = await apiClient.get<unknown>(submissionRoute(kind), {
        params: { exam, edition },
      });
      return fromApiSubmission(kind, data);
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 404) {
        return null;
      }
      return toProfileRequired(error);
    }
  }
}
